import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
  Alert,
  Box,
} from '@mui/material';
import { Calculate } from '@mui/icons-material';

// Popup con la visualización del split de facturación (CLAUDE.md §5, decisión
// PO 2026-09-15). Reemplaza la página /calculadora: el % H ahora se edita en la
// pantalla del presupuesto y este modal es sólo para mostrar/screenshotear el
// split al cliente. Sin selector de presupuesto, sin guardado - recibe los
// números ya calculados de la versión en contexto y hace la MISMA cuenta que
// hacía CalculadoraH.tsx (líneas 183-211 del archivo original).
interface CalculadoraHModalProps {
  open: boolean;
  onClose: () => void;
  materialesUsd: number;
  ivaPercentage: number;
  porcentajeH: number;              // 0-100, el H persistido en la versión
  hFxRate: number | null;          // h_fx_rate de la versión
  hFxCurrency?: string | null;     // moneda local, ej 'ARS'
}

const CalculadoraHModal: React.FC<CalculadoraHModalProps> = ({
  open,
  onClose,
  materialesUsd,
  ivaPercentage,
  porcentajeH,
  hFxRate,
  hFxCurrency,
}) => {
  const porcentajeFacturado = 100 - porcentajeH;
  const ivaFraction = ivaPercentage / 100;
  const dolarActual = hFxRate;
  const localCurrency = hFxCurrency || 'ARS';

  // Valores en USD
  const facturadoUSD = (materialesUsd * porcentajeFacturado) / 100;
  const ivaFacturadoUSD = facturadoUSD * ivaFraction;
  const facturadoConIvaUSD = facturadoUSD + ivaFacturadoUSD;
  const hUSD = (materialesUsd * porcentajeH) / 100;
  const totalConIvaUSD = facturadoConIvaUSD + hUSD;

  // Valores en moneda local (sólo si hay cotización H disponible)
  const facturadoLocal = dolarActual != null ? facturadoUSD * dolarActual : null;
  const ivaFacturadoLocal = facturadoLocal != null ? facturadoLocal * ivaFraction : null;
  const facturadoConIvaLocal = facturadoLocal != null && ivaFacturadoLocal != null
    ? facturadoLocal + ivaFacturadoLocal : null;
  const hLocal = dolarActual != null ? hUSD * dolarActual : null;
  const totalConIvaLocal = facturadoConIvaLocal != null && hLocal != null
    ? facturadoConIvaLocal + hLocal : null;

  const formatCurrency = (value: number, currency: string = 'USD'): string =>
    new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Calculate color="primary" />
        Calculadora H — Split de facturación
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Materiales (sin IVA)
            </Typography>
            <Typography variant="h6" color="success.main" fontWeight="bold">
              {formatCurrency(materialesUsd, 'USD')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Facturado {porcentajeFacturado.toFixed(2)}% · H {porcentajeH.toFixed(2)}%
            </Typography>
          </Box>

          {dolarActual == null && (
            <Alert severity="warning">
              No hay una cotización disponible para esta versión todavía - la conversión a{' '}
              {localCurrency} no se puede mostrar hasta que se guarde el % H al menos una vez.
            </Alert>
          )}

          {/* Tabla USD */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Concepto</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Monto USD</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>% del Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Facturado ({porcentajeFacturado.toFixed(2)}%)</TableCell>
                  <TableCell align="right">{formatCurrency(facturadoUSD, 'USD')}</TableCell>
                  <TableCell align="right">{porcentajeFacturado.toFixed(2)}%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>IVA Facturado ({ivaPercentage.toFixed(2)}%)</TableCell>
                  <TableCell align="right">{formatCurrency(ivaFacturadoUSD, 'USD')}</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: 'info.light' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Facturado + IVA</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(facturadoConIvaUSD, 'USD')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>H - Sin Factura ({porcentajeH.toFixed(2)}%)</TableCell>
                  <TableCell align="right">{formatCurrency(hUSD, 'USD')}</TableCell>
                  <TableCell align="right">{porcentajeH.toFixed(2)}%</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: 'success.light' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totalConIvaUSD, 'USD')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Tabla en moneda local */}
          {dolarActual != null && facturadoLocal != null && ivaFacturadoLocal != null
            && facturadoConIvaLocal != null && hLocal != null && totalConIvaLocal != null && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                      {`Concepto (Cotización: ${dolarActual.toFixed(2)})`}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Monto {localCurrency}</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>% del Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Facturado ({porcentajeFacturado.toFixed(2)}%)</TableCell>
                    <TableCell align="right">{formatCurrency(facturadoLocal, localCurrency)}</TableCell>
                    <TableCell align="right">{porcentajeFacturado.toFixed(2)}%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 4 }}>IVA Facturado ({ivaPercentage.toFixed(2)}%)</TableCell>
                    <TableCell align="right">{formatCurrency(ivaFacturadoLocal, localCurrency)}</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: 'info.light' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Facturado + IVA</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(facturadoConIvaLocal, localCurrency)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>H - Sin Factura ({porcentajeH.toFixed(2)}%)</TableCell>
                    <TableCell align="right">{formatCurrency(hLocal, localCurrency)}</TableCell>
                    <TableCell align="right">{porcentajeH.toFixed(2)}%</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: 'success.light' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totalConIvaLocal, localCurrency)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalculadoraHModal;
